import React, { useState, useEffect } from 'react';
import { companyAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Building2, Users, CheckCircle, AlertTriangle, Loader2, RefreshCw,
  Wifi, WifiOff, CreditCard, ArrowRight, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

export default function BankingManagement() {
  const [status, setStatus] = useState(null);
  const [empStatus, setEmpStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // employee id or 'bulk'

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        companyAdminAPI.getBankingStatus(),
        companyAdminAPI.getEmployeesBankingStatus(),
      ]);
      setStatus(s);
      setEmpStatus(e);
    } catch (err) { toast.error('Failed to load banking status'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreateAccount = async (empId, empName) => {
    setCreating(empId);
    try {
      const res = await companyAdminAPI.createEmployeeBankAccount(empId);
      toast.success(`Account created for ${empName}: ${res.account_number}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally { setCreating(null); }
  };

  const handleBulkCreate = async () => {
    setCreating('bulk');
    try {
      const res = await companyAdminAPI.bulkCreateBankAccounts();
      toast.success(`Created ${res.summary.total_created} accounts. ${res.summary.total_failed} failed, ${res.summary.total_skipped} skipped.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk creation failed');
    } finally { setCreating(null); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
    </div>
  );

  const summary = empStatus?.summary || {};

  return (
    <div className="space-y-6" data-testid="banking-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Banking & Wallets</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage employee Assets MFB wallet accounts</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-banking">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Connection Status */}
      <Card className={`border-2 ${status?.reachable ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
        <CardContent className="p-4 flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status?.reachable ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            {status?.reachable ? <Wifi className="w-5 h-5 text-emerald-600" /> : <WifiOff className="w-5 h-5 text-amber-600" />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">{status?.provider}</p>
            <p className={`text-xs ${status?.reachable ? 'text-emerald-600' : 'text-amber-600'}`}>
              {status?.message}
            </p>
          </div>
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
            status?.reachable ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
          }`} data-testid="banking-mode-badge">
            {status?.mode}
          </span>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="w-4.5 h-4.5 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="accounts-created-count">{summary.accounts_created}</p>
            <p className="text-xs text-gray-500">Accounts Created</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center mx-auto mb-2">
              <CreditCard className="w-4.5 h-4.5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="ready-count">{summary.ready}</p>
            <p className="text-xs text-gray-500">Ready to Create</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="w-4.5 h-4.5 text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="missing-bvn-count">{summary.missing_bvn}</p>
            <p className="text-xs text-gray-500">Missing BVN</p>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Create */}
      {summary.ready > 0 && (
        <Card className="border-pink-200 bg-pink-50/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-pink-500" />
              <div>
                <p className="text-sm font-semibold text-gray-900">{summary.ready} employees ready for wallet creation</p>
                <p className="text-xs text-gray-500">Create Assets MFB accounts for all eligible employees at once</p>
              </div>
            </div>
            <Button
              onClick={handleBulkCreate}
              disabled={creating === 'bulk' || !status?.reachable}
              className="bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="bulk-create-btn"
            >
              {creating === 'bulk' ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create All <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Employees with accounts */}
      {empStatus?.with_account?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" /> Active Wallet Accounts
          </h3>
          <div className="space-y-1.5">
            {empStatus.with_account.map(emp => (
              <div key={emp.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100" data-testid={`account-row-${emp.id}`}>
                <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Building2 className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                  <p className="text-xs text-gray-400 truncate">{emp.email}</p>
                </div>
                <span className="text-xs font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">{emp.account_number}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ready to create */}
      {empStatus?.ready_to_create?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-blue-500" /> Ready to Create
          </h3>
          <div className="space-y-1.5">
            {empStatus.ready_to_create.slice(0, 10).map(emp => (
              <div key={emp.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100" data-testid={`ready-row-${emp.id}`}>
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                  <Users className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                  <p className="text-xs text-gray-400 truncate">{emp.email}</p>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => handleCreateAccount(emp.id, emp.name)}
                  disabled={creating === emp.id || !status?.reachable}
                  className="h-7 text-xs"
                  data-testid={`create-account-${emp.id}`}
                >
                  {creating === emp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create'}
                </Button>
              </div>
            ))}
            {empStatus.ready_to_create.length > 10 && (
              <p className="text-xs text-gray-400 text-center py-1">and {empStatus.ready_to_create.length - 10} more...</p>
            )}
          </div>
        </div>
      )}

      {/* Missing BVN */}
      {empStatus?.needs_bvn?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Missing BVN — Cannot Create Account
          </h3>
          <div className="space-y-1.5">
            {empStatus.needs_bvn.slice(0, 5).map(emp => (
              <div key={emp.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-amber-50/50 border border-amber-100" data-testid={`missing-bvn-${emp.id}`}>
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                  <p className="text-xs text-amber-600">BVN required to create wallet account</p>
                </div>
              </div>
            ))}
            {empStatus.needs_bvn.length > 5 && (
              <p className="text-xs text-gray-400 text-center py-1">and {empStatus.needs_bvn.length - 5} more employees need BVN updates</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
